import React, { Component } from 'react'
import {
    Badge,
    Button,
    Card,
    Col,
    Form,
    Row,
    DatePicker,
    message,
    Modal,
    Spin,
    Typography,
    Select,
    Table,
    Tag,
    Empty,
    Skeleton,
} from 'antd'
import { connect } from 'react-redux'
import moment from 'moment'
import {
    ReloadOutlined,
    FileTextOutlined,
    DownloadOutlined
} from '@ant-design/icons'
import AssetSearch from '../../components/AssetSearch'
import RectifyCell from '../../components/RectifyCell'
import { fetchAssetFiltersData, processDailyFuelData, searchGlobalAssetData } from '../../appRedux/actions'
import axios from '../../util/Api'
import { groupFuelDataByGroupingName } from '../DeviceMap/fuel-report/fuelDataGroup'
import { saveAs } from 'file-saver'
import json2csv from 'json2csv'

// Component to handle async data grouping for expanded rows
const ExpandedRowContent = ({ data, interval, groupFuelDataByGroupingName }) => {
    const [groupedData, setGroupedData] = React.useState(null)
    const [loading, setLoading] = React.useState(true)

    React.useEffect(() => {
        const processData = async () => {
            try {
                setLoading(true)
                const result = await groupFuelDataByGroupingName(data, interval)
                setGroupedData(result)
            } catch (error) {
                console.error('Error grouping data:', error)
                setGroupedData([])
            } finally {
                setLoading(false)
            }
        }

        processData()
    }, [data, interval, groupFuelDataByGroupingName])

    if (loading) {
        return <div style={{ padding: '20px', textAlign: 'center' }}><Spin /> Loading grouped data...</div>
    }

    if (!groupedData || groupedData.length === 0) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>No grouped data available</div>
    }

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (date) => <div>{date}</div>
        },
        {
            title: 'Fuel Consumed (ltrs)',
            dataIndex: 'fuel_consumed',
            key: 'fuel_consumed',
            width: 150,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Distance Travelled (km)',
            dataIndex: 'distance_travelled',
            key: 'distance_travelled',
            width: 150,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Idle Time (min)',
            dataIndex: 'idle_time_min',
            key: 'idle_time_min',
            width: 130,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Moving Time (min)',
            dataIndex: 'moving_time_min',
            key: 'moving_time_min',
            width: 130,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Ignition Time (min)',
            dataIndex: 'ignition_time_min',
            key: 'ignition_time_min',
            width: 130,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: "Speed (KM/HR)",
            dataIndex: 'avg_speed',
            key: 'avg_speed',
            width: 130,
            render: (_record) => <div>{_record.ignition_time_min && _record.distance_travelled ? Number(Number(_record.distance_travelled) / (Number(_record.ignition_time_min) / 60)).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Possible Refuel',
            dataIndex: 'total_refuel',
            key: 'total_refuel',
            width: 120,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Possible Drain',
            dataIndex: 'total_theft',
            key: 'total_theft',
            width: 120,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Consumption Rate (KMS/Ltr)',
            dataIndex: 'consumption_rate',
            key: 'consumption_rate',
            width: 140,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Consumption Rate (Ltr/KMS)',
            dataIndex: 'consumption_rate',
            key: 'consumption_rate',
            width: 140,
            render: (_, record) => <div>{record.consumption_rate ? Number(1 / record.consumption_rate).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Consumption Rate (Hours/Ltr)',
            dataIndex: 'consumption_rate_ignition',
            key: 'consumption_rate_ignition',
            width: 140,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Consumption Rate (Ltr/Hours)',
            dataIndex: 'consumption_rate_ignition',
            key: 'consumption_rate_ignition',
            width: 140,
            render: (_, record) => <div>{record.consumption_rate_ignition ? Number(1 / record.consumption_rate_ignition).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Manual Refuel Consumption Rate (KMS/Ltr)',
            dataIndex: 'consumption_rate_manual_refuel',
            key: 'consumption_rate_manual_refuel',
            width: 140,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Manual Refuel Consumption Rate (Ltr/KMS)',
            dataIndex: 'consumption_rate_manual_refuel',
            key: 'consumption_rate_manual_refuel',
            width: 140,
            render: (_, record) => <div>{record.consumption_rate_manual_refuel ? Number(1 / record.consumption_rate_manual_refuel).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Manual Refuel Consumption Rate (Hours/Ltr)',
            dataIndex: 'consumption_rate_manual_refuel_hours',
            key: 'consumption_rate_manual_refuel_hours',
            width: 140,
            render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
        },
        {
            title: 'Manual Refuel Consumption Rate (Ltr/Hours)',
            dataIndex: 'consumption_rate_manual_refuel_hours',
            key: 'consumption_rate_manual_refuel_hours',
            width: 140,
            render: (_, record) => <div>{record.consumption_rate_manual_refuel_hours ? Number(1 / record.consumption_rate_manual_refuel_hours).toFixed(2) : '0.00'}</div>
        }
    ]

    return <Table columns={columns} bordered dataSource={groupedData} pagination={false} scroll={{ x: 1200 }} />
}

const { Text, Title } = Typography
const { Option } = Select
const { RangePicker } = DatePicker

function formatAssetLabel (asset) {
    if (!asset) return 'Asset'
    const a = asset.assetCode || '-'
    const b = asset.altName || '-'
    const c = asset.ownerCode || '-'
    return `${a} / ${b} / ${c}`
}

function mergeGreenDrivingSummary(children = []) {
    const counts = { 1: 0, 2: 0, 3: 0 }
    const logs = { 1: [], 2: [], 3: [] }
    children.forEach((item) => {
        const g = item.green_driving_summary || item.green_drivng_summery
        if (!g) return
        if (g.counts) {
            [1, 2, 3].forEach(k => { counts[k] = (counts[k] || 0) + (g.counts[k] || 0) })
        }
        if (g.logs) {
            [1, 2, 3].forEach(k => { if (Array.isArray(g.logs[k])) logs[k] = logs[k].concat(g.logs[k]) })
        }
    })
    return { counts, logs }
}

function mergeOverspeedSummary(children = []) {
    let total_overspeed_events = 0
    const overspeed_events = []
    children.forEach((item) => {
        const o = item.overspeed_summary
        if (!o) return
        total_overspeed_events += Number(o.total_overspeed_events || 0)
        if (Array.isArray(o.overspeed_events)) overspeed_events.push(...o.overspeed_events)
    })
    return { speed_limit: null, total_overspeed_events, overspeed_events }
}

class IotDeviceReport extends Component {
    state = {
        selectedAsset: null,
        interval: 'd',
        lastReportPayload: null,
        expandedRowKeys: [],
        rectifiedRecordInfo: null, // Store info about the rectified record to auto-expand after refresh
        reportLoading: false, // Track report generation loading state
        greenDrivingModalRecord: null,
        overspeedModalRecord: null
    }

    componentDidMount() {
        this.props.fetchAssetFiltersData()
    }

    handleRectifySuccess = async (record, field, newValue) => {
        // Show success message first
        message.success('Value rectified successfully. Regenerating report...')

        // Store the rectified record info to auto-expand after regeneration
        const rectifiedRecordInfo = {
            assetId: record.asset && record.asset._id,
            dateTimestamp: record.date_timestamp
        }
        this.setState({ rectifiedRecordInfo })

        // Regenerate the report with the same filters
        const { lastReportPayload } = this.state
        if (lastReportPayload) {
            try {
                await this.generateReport(lastReportPayload, true)
            } catch (error) {
                message.error('Failed to regenerate report after rectification')
            }
        } else {
            message.warning('Please regenerate the report manually to see updated values')
        }
    }

    handleSubmit = e => {
        e.preventDefault()
        e.stopPropagation()
        this.props.form.validateFields((err, values) => {
            if (err) {
                // Form validation failed
            } else {
                if (moment(values.periodFromTo[0]).isSameOrBefore(values.periodFromTo[1])) {
                    const payload = {
                        assets: this.state.selectedAsset,
                        locations: values.location,
                        subLocations: values.subLocation,
                        assetTypes: values.assetType,
                        assetSubTypes: values.assetSubType,
                        assetModels: values.assetModel,
                        periodFrom: moment(values.periodFromTo[0]).startOf('day').toDate(),
                        periodTo: moment(values.periodFromTo[1]).endOf('day').toDate(),
                        frequency: values.frequency || 'Daily'
                    }

                    // Call API to generate report
                    this.generateReport(payload, false)
                } else {
                    message.error("Period From couldn't come earlier than Period To!")
                }
            }
        })
    }

    handleAssetSelect = (val) => {
        // console.log('handleAssetSelect called with val:', val)
        // Handle both single and multiple selections
        if (val) {
            this.setState({
                selectedAsset: val
            })
        }
    }

    generateReport = async (payload, isRegenerating = false) => {
        this.setState({ reportLoading: true })
        try {

            // console.log('Generating report with payload:', payload)
            const { periodFrom, periodTo, ...rest } = payload

            // console.log('Period From:', moment(periodFrom).startOf('day').valueOf())
            // console.log('Period To:', moment(periodto).startOf('day').valueOf())

            const response = await axios.post(`/daily-fuel-data/all-device-report/${moment(periodFrom).startOf('day').valueOf()}/${moment(periodTo).endOf('day').valueOf()}`, rest)
            const { data } = response

            // group the data by asset 
            const assetGroups = {}
            const processedData = processDailyFuelData({ data }, true)
            // console.log('Processed Data:', processedData)
            const allAssets = new Map()
            // Group data by asset
            processedData.forEach(item => {
                allAssets.set(item.asset && item.asset._id || 'unknown', item.asset)
                const assetKey = item.asset && item.asset._id || 'unknown'
                if (!assetGroups[assetKey]) {
                    assetGroups[assetKey] = []
                }
                assetGroups[assetKey].push(item)
            })

            // console.log('Asset Groups:', assetGroups)

            // Apply groupFuelDataByGroupingName to each asset group
            const groupedData = {}
            for (const [assetKey, assetData] of Object.entries(assetGroups)) {
                try {
                    // console.log(`Processing asset: ${assetKey} with ${assetData.length} records`)
                    const groupedAssetData = await groupFuelDataByGroupingName(assetData, 'd')
                    // console.log('Grouped Asset Data:', groupedAssetData)
                    groupedData[assetKey] = groupedAssetData
                } catch (error) {
                    console.error(`Error processing asset ${assetKey}:`, error)
                    // Continue with other assets even if one fails
                    groupedData[assetKey] = []
                }
            }

            // console.log('Grouped Data:', groupedData)
            let reportData = []
            for (const [assetKey, docs] of Object.entries(groupedData)) {
                const asset = allAssets.get(assetKey)
                // Get the original data for this asset to use as children
                const assetOriginalData = assetGroups[assetKey]

                reportData = reportData.concat(docs.map((doc, index) => {
                    const children = assetOriginalData || []
                    const green_driving_summary = mergeGreenDrivingSummary(children)
                    const overspeed_summary = mergeOverspeedSummary(children)
                    return {
                        ...doc,
                        assetId: assetKey,
                        asset: asset,
                        childrenData: assetOriginalData,
                        green_driving_summary,
                        overspeed_summary,
                        key: `${assetKey}-${index}`
                    }
                }))
            }

            // Store rectifiedRecordInfo before setState (it might be cleared in callback)
            const rectifiedRecordInfo = this.state.rectifiedRecordInfo

            this.setState({ reportData: reportData, lastReportPayload: payload }, () => {
                // Auto-expand row if rectifiedRecordInfo was set (use closure variable)
                if (rectifiedRecordInfo) {
                    const { assetId, dateTimestamp } = rectifiedRecordInfo
                    const rowToExpand = reportData.find(record => {
                        return record.assetId === assetId &&
                            record.childrenData &&
                            record.childrenData.some(child =>
                                child.date_timestamp === dateTimestamp
                            )
                    })

                    if (rowToExpand) {
                        // Use setTimeout to ensure the table has rendered before expanding
                        setTimeout(() => {
                            this.setState({
                                expandedRowKeys: [rowToExpand.key],
                                rectifiedRecordInfo: null // Clear after expanding
                            })
                        }, 100)
                    } else {
                        this.setState({ rectifiedRecordInfo: null })
                    }
                }
            })
            // console.log('Report Data:', reportData)

            this.setState({ reportLoading: false })
            message.success(isRegenerating ? 'Report regenerated successfully' : 'Report generated successfully')
        } catch (error) {
            console.error('Error generating report:', error)
            this.setState({ reportLoading: false })
            message.error('Failed to generate report. Please try again.')
        }
    }

    expandedRowRender = (record) => {
        // console.log('expandedRowRender called with record:', record)
        // console.log('record.childrenData:', record.childrenData)
        let data = record.childrenData
        if (!data || data.length === 0) {
            return <div>No detailed data available for this asset</div>
        }

        // If interval is 'd', use data directly, otherwise show loading initially
        if (this.state.interval === 'd') {
            return this.renderExpandedTable(data, record)
        } else {
            // For non-daily intervals, we need to handle async grouping
            return <ExpandedRowContent
                data={data}
                interval={this.state.interval}
                groupFuelDataByGroupingName={groupFuelDataByGroupingName}
            />
        }
    }

    renderExpandedTable = (data, parentRecord) => {
        const columns = [
            {
                title: 'Date',
                dataIndex: 'date_timestamp',
                key: 'date_timestamp',
                width: 120,
                render: (date_timestamp) => <div>{moment(date_timestamp).format('DD-MM-YYYY')}</div>
            },
            {
                title: 'Asset Code',
                dataIndex: 'assetCode',
                key: 'assetCode',
                width: 120,
                render: (value, record) => <div>{record.asset && record.asset.assetCode || 'N/A'}</div>
            },
            {
                title: 'Data Available (hrs.)',
                dataIndex: 'data_available',
                key: 'data_available',
                width: 80,
                render: (_, record) => {
                    // console.log('record - ', record)
                    const { dataFromTimestamp, dataToTimestamp } = record || {};
                    if (dataFromTimestamp && dataToTimestamp) {
                        return <div>{((dataToTimestamp - dataFromTimestamp) / 1000 / 60 / 60).toFixed(2)}</div>
                    }
                    return <div>N/A</div>
                }
            },
            {
                title: 'Sensor Based Fuel Consumed (ltrs)',
                dataIndex: 'fuel_consumed',
                key: 'fuel_consumed',
                width: 150,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Sensor Starting Fuel (ltrs)',
                dataIndex: 'start_fuel_level_liters',
                key: 'start_fuel_level_liters',
                width: 150,
                render: (value, record) => (
                    <RectifyCell
                        record={record}
                        field="start_fuel_level_liters"
                        value={value}
                        onSuccess={this.handleRectifySuccess}
                        roles={this.props.authUser ? this.props.authUser.roles : []}
                    />
                )
            },
            {
                title: 'Sensor Ending Fuel (ltrs)',
                dataIndex: 'end_fuel_level_liters',
                key: 'end_fuel_level_liters',
                width: 150,
                render: (value, record) => (
                    <RectifyCell
                        record={record}
                        field="end_fuel_level_liters"
                        value={value}
                        onSuccess={this.handleRectifySuccess}
                        roles={this.props.authUser ? this.props.authUser.roles : []}
                    />
                )
            },
            {
                title: 'Possible Refuel',
                dataIndex: 'total_refuel',
                key: 'total_refuel',
                width: 120,
                render: (value, record) => (
                    <RectifyCell
                        record={record}
                        field="total_refuel"
                        value={value}
                        onSuccess={this.handleRectifySuccess}
                        roles={this.props.authUser ? this.props.authUser.roles : []}
                    />
                )
            },
            {
                title: 'Possible Drain',
                dataIndex: 'total_theft',
                key: 'total_theft',
                width: 120,
                render: (value, record) => (
                    <RectifyCell
                        record={record}
                        field="total_theft"
                        value={value}
                        onSuccess={this.handleRectifySuccess}
                        roles={this.props.authUser ? this.props.authUser.roles : []}
                    />
                )
            },
            {
                title: 'Distance Travelled (km)',
                dataIndex: 'distance_travelled',
                key: 'distance_travelled',
                width: 150,
                render: (value, record) => (
                    <RectifyCell
                        record={record}
                        field="distance_travelled"
                        value={value}
                        onSuccess={this.handleRectifySuccess}
                        roles={this.props.authUser ? this.props.authUser.roles : []}
                    />
                )
            },
            {
                title: 'Idle Time (min)',
                dataIndex: 'idle_time_min',
                key: 'idle_time_min',
                width: 130,
                render: (value, record) => (
                    <RectifyCell
                        record={record}
                        field="ideal_time"
                        value={value}
                        onSuccess={this.handleRectifySuccess}
                        roles={this.props.authUser ? this.props.authUser.roles : []}
                        convertMinToMs={true}
                    />
                )
            },
            {
                title: 'Moving/Working Time (min)',
                dataIndex: 'moving_time_min',
                key: 'moving_time_min',
                width: 130,
                render: (value, record) => (
                    <RectifyCell
                        record={record}
                        field="moving_time"
                        value={value}
                        onSuccess={this.handleRectifySuccess}
                        roles={this.props.authUser ? this.props.authUser.roles : []}
                        convertMinToMs={true}
                    />
                )
            },
            {
                title: 'Ignition Time (min)',
                dataIndex: 'ignition_time_min',
                key: 'ignition_time_min',
                width: 130,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: "Speed (KM/HR)",
                dataIndex: 'speed',
                key: 'speed',
                width: 130,
                render: (_, record) => <div>{record.ignition_time_min && record.distance_travelled ? Number(Number(record.distance_travelled) / (Number(record.ignition_time_min) / 60)).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Consumption Rate (KMS/Ltr)',
                dataIndex: 'consumption_rate',
                key: 'consumption_rate',
                width: 140,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Consumption Rate (Ltr/KMS)',
                dataIndex: 'consumption_rate',
                key: 'consumption_rate',
                width: 140,
                render: (_, record) => <div>{record.consumption_rate ? Number(record.consumption_rate > 0 ? 1 / record.consumption_rate : 0).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Consumption Rate (Hours/Ltr)',
                dataIndex: 'consumption_rate_ignition',
                key: 'consumption_rate_ignition',
                width: 140,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Consumption Rate (Ltr/Hours)',
                dataIndex: 'consumption_rate_ignition',
                key: 'consumption_rate_ignition',
                width: 140,
                render: (_, record) => <div>{record.consumption_rate_ignition ? Number(record.consumption_rate_ignition > 0 ? 1 / record.consumption_rate_ignition : 0).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Manual Refuel (ltrs)',
                dataIndex: 'manualRefualQuantity',
                key: 'manualRefualQuantity',
                width: 100,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Manual Based Fuel Consumed (ltrs)',
                dataIndex: 'manual_based_fuel_consumed',
                key: 'manual_based_fuel_consumed',
                width: 150,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Manual Refuel Consumption Rate (KMS/Ltr)',
                dataIndex: 'consumption_rate_manual_refuel',
                key: 'consumption_rate_manual_refuel',
                width: 140,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Manual Refuel Consumption Rate (Ltr/KMS)',
                dataIndex: 'consumption_rate',
                key: 'consumption_rate',
                width: 140,
                render: (_, record) => <div>{record.consumption_rate_manual_refuel ? Number(record.consumption_rate_manual_refuel > 0 ? 1 / record.consumption_rate_manual_refuel : 0).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Manual Refuel Consumption Rate (Hours/Ltr)',
                dataIndex: 'consumption_rate_manual_refuel_hours',
                key: 'consumption_rate_manual_refuel_hours',
                width: 140,
                render: (value) => <div>{value ? Number(value).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Manual Refuel Consumption Rate (Ltr/Hours)',
                dataIndex: 'consumption_rate_manual_refuel_hours',
                key: 'consumption_rate_manual_refuel_hours',
                width: 140,
                render: (_, record) => <div>{record.consumption_rate_manual_refuel_hours ? Number(record.consumption_rate_manual_refuel_hours > 0 ? 1 / record.consumption_rate_manual_refuel_hours : 0).toFixed(2) : '0.00'}</div>
            },
            {
                title: 'Green Driving',
                dataIndex: 'green_driving_summary',
                key: 'green_driving',
                width: 120,
                render: (_, record) => {
                    const g = record.green_driving_summary || record.green_drivng_summery
                    if (!g) return null
                    const total = g && g.counts ? (Number(g.counts[1] || 0) + Number(g.counts[2] || 0) + Number(g.counts[3] || 0)) : 0
                    if (total > 0) {
                        const modalRecord = parentRecord ? { ...record, asset: parentRecord.asset } : record
                        return (
                            <a onClick={(e) => { e.preventDefault(); this.setState({ greenDrivingModalRecord: modalRecord }) }} href="#">
                                <Badge count={total} />
                            </a>
                        )
                    }
                    return <Text>0</Text>
                }
            },
            {
                title: 'Overspeeding',
                dataIndex: 'overspeed_summary',
                key: 'overspeeding',
                width: 120,
                render: (_, record) => {
                    const o = record.overspeed_summary
                    if (!o) return null
                    const total = Number(o.total_overspeed_events || 0)
                    if (total > 0) {
                        const modalRecord = parentRecord ? { ...record, asset: parentRecord.asset } : record
                        return (
                            <a onClick={(e) => { e.preventDefault(); this.setState({ overspeedModalRecord: modalRecord }) }} href="#">
                                <Badge count={total} />
                            </a>
                        )
                    }
                    return <Text>0</Text>
                }
            }
        ];

        return <Table columns={columns} bordered dataSource={data || []} pagination={false} scroll={{ x: 2700 }} />;
    }

    downloadReport = (reportData) => {
        if (reportData && reportData.length) {
            try {
                // Get all asset groups data for download
                const allDownloadData = []

                // Iterate through each report record to get the children data
                reportData.forEach(record => {
                    if (record.childrenData && record.childrenData.length > 0) {
                        // Add asset information to each record
                        const assetInfo = record.asset || {}
                        record.childrenData.forEach(childRecord => {
                            const { dataFromTimestamp, dataToTimestamp } = childRecord || {};
                            const dataAvailable = dataFromTimestamp && dataToTimestamp ? ((dataToTimestamp - dataFromTimestamp) / 1000 / 60 / 60).toFixed(2) : 'NA';
                            allDownloadData.push({
                                date: childRecord.date_timestamp ? moment(childRecord.date_timestamp).format('DD-MM-YYYY') : 'NA',
                                assetCode: assetInfo.assetCode || 'NA',
                                altName: assetInfo.altName || 'NA',
                                ownerCode: assetInfo.ownerCode || 'NA',
                                location: assetInfo.location && assetInfo.location.name || 'NA',
                                subLocation: assetInfo.client && assetInfo.client.name || 'NA',
                                assetType: assetInfo.equipmentSubType && assetInfo.equipmentSubType.assetType && assetInfo.equipmentSubType.assetType.name || 'NA',
                                assetSubType: assetInfo.equipmentSubType && assetInfo.equipmentSubType.name || 'NA',
                                assetModel: assetInfo.assetModel && assetInfo.assetModel.name || 'NA',
                                dataAvailable: dataAvailable,
                                fuelConsumed: childRecord.fuel_consumed ? Number(childRecord.fuel_consumed).toFixed(2) : '0.00',
                                distanceTravelled: childRecord.distance_travelled ? Number(childRecord.distance_travelled).toFixed(2) : '0.00',
                                idleTime: childRecord.idle_time_min ? Number(childRecord.idle_time_min).toFixed(2) : '0.00',
                                movingTime: childRecord.moving_time_min ? Number(childRecord.moving_time_min).toFixed(2) : '0.00',
                                ignitionTime: childRecord.ignition_time_min ? Number(childRecord.ignition_time_min).toFixed(2) : '0.00',
                                speed: childRecord.ignition_time_min && childRecord.distance_travelled ? Number(Number(childRecord.distance_travelled) / (Number(childRecord.ignition_time_min) / 60)).toFixed(2) : '0.00',
                                startFuelLevel: childRecord.start_fuel_level_liters ? Number(childRecord.start_fuel_level_liters).toFixed(2) : '0.00',
                                endFuelLevel: childRecord.end_fuel_level_liters ? Number(childRecord.end_fuel_level_liters).toFixed(2) : '0.00',
                                possibleRefuel: childRecord.total_refuel ? Number(childRecord.total_refuel).toFixed(2) : '0.00',
                                possibleDrain: childRecord.total_theft ? Number(childRecord.total_theft).toFixed(2) : '0.00',
                                consumptionRateKms: childRecord.consumption_rate ? Number(childRecord.consumption_rate).toFixed(2) : '0.00',
                                consumptionRateHours: childRecord.consumption_rate_ignition ? Number(childRecord.consumption_rate_ignition).toFixed(2) : '0.00',
                                manualRefuelQuantity: childRecord.manualRefualQuantity ? Number(childRecord.manualRefualQuantity).toFixed(2) : '0.00',
                                manualBasedFuelConsumed: childRecord.manual_based_fuel_consumed ? Number(childRecord.manual_based_fuel_consumed).toFixed(2) : '0.00',

                                consumptionRateManualRefuel: childRecord.consumption_rate_manual_refuel ? Number(childRecord.consumption_rate_manual_refuel).toFixed(2) : '0.00',
                                consumptionRateManualRefuelHours: childRecord.consumption_rate_manual_refuel_hours ? Number(childRecord.consumption_rate_manual_refuel_hours).toFixed(2) : '0.00',
                                consumptionRateManualRefuelLtrKms: childRecord.consumption_rate_manual_refuel ? Number(childRecord.consumption_rate_manual_refuel > 0 ? (1 / childRecord.consumption_rate_manual_refuel) : 0).toFixed(2) : '0.00',
                                consumptionRateManualRefuelHoursLtr: childRecord.consumption_rate_manual_refuel_hours ? Number(childRecord.consumption_rate_manual_refuel_hours > 0 ? (1 / childRecord.consumption_rate_manual_refuel_hours) : 0).toFixed(2) : '0.00',
                                consumptionRateKmsLtr: childRecord.consumption_rate ? Number(childRecord.consumption_rate).toFixed(2) : '0.00',
                                consumptionRateLtrKms: childRecord.consumption_rate ? Number(childRecord.consumption_rate > 0 ? (1 / childRecord.consumption_rate) : 0).toFixed(2) : '0.00',
                                consumptionRateHoursLtr: childRecord.consumption_rate_ignition ? Number(childRecord.consumption_rate_ignition).toFixed(2) : '0.00',
                                consumptionRateLtrHours: childRecord.consumption_rate_ignition ? Number(childRecord.consumption_rate_ignition > 0 ? 1 / childRecord.consumption_rate_ignition : 0).toFixed(2) : '0.00',

                                greenDriving: (() => {
                                    const g = childRecord.green_driving_summary || childRecord.green_drivng_summery
                                    return g && g.counts ? (Number(g.counts[1] || 0) + Number(g.counts[2] || 0) + Number(g.counts[3] || 0)) : 0
                                })(),
                                overspeeding: (() => {
                                    const o = childRecord.overspeed_summary
                                    return o ? Number(o.total_overspeed_events || 0) : 0
                                })()
                            })
                        })
                    }
                })

                // console.log('Download data:', allDownloadData)

                // Create summary data
                const summaryData = {
                    totalRecords: allDownloadData.length,
                    totalFuelConsumed: allDownloadData.reduce((sum, record) => sum + Number(record.fuelConsumed), 0).toFixed(2),
                    totalDistanceTravelled: allDownloadData.reduce((sum, record) => sum + Number(record.distanceTravelled), 0).toFixed(2),
                    totalIdleTime: allDownloadData.reduce((sum, record) => sum + Number(record.idleTime), 0).toFixed(2),
                    totalMovingTime: allDownloadData.reduce((sum, record) => sum + Number(record.movingTime), 0).toFixed(2),
                    uniqueAssets: [...new Set(allDownloadData.map(record => record.assetCode))].length
                }

                // Define CSV fields
                const fields = [
                    { label: 'Date', value: 'date' },
                    { label: 'Asset Code', value: 'assetCode' },
                    { label: 'Alt Name', value: 'altName' },
                    { label: 'Owner Code', value: 'ownerCode' },
                    { label: 'Location', value: 'location' },
                    { label: 'Sub Location', value: 'subLocation' },
                    { label: 'Asset Type', value: 'assetType' },
                    { label: 'Asset Sub Type', value: 'assetSubType' },
                    { label: 'Asset Model', value: 'assetModel' },
                    { label: 'Data Available (hours)', value: 'dataAvailable' },
                    { label: 'Fuel Consumed (ltrs)', value: 'fuelConsumed' },
                    { label: 'Distance Travelled (km)', value: 'distanceTravelled' },

                    { label: 'Idle Time (min)', value: 'idleTime' },
                    { label: 'Moving Time (min)', value: 'movingTime' },
                    { label: 'Ignition Time (min)', value: 'ignitionTime' },

                    { label: 'Speed (KM/HR)', value: 'speed' },

                    { label: 'Sensor Starting Fuel (ltrs)', value: 'startFuelLevel' },
                    { label: 'Sensor Ending Fuel (ltrs)', value: 'endFuelLevel' },

                    { label: 'Possible Refuel', value: 'possibleRefuel' },
                    { label: 'Possible Drain', value: 'possibleDrain' },
                    // { label: 'Consumption Rate (KMS)', value: 'consumptionRateKms' },
                    // { label: 'Consumption Rate (Hours)', value: 'consumptionRateHours' },

                    { label: 'Consumption Rate (KMS/Ltr)', value: 'consumptionRateKmsLtr' },
                    { label: 'Consumption Rate (Ltr/KMS)', value: 'consumptionRateLtrKms' },
                    { label: 'Consumption Rate (Hours/Ltr)', value: 'consumptionRateHoursLtr' },
                    { label: 'Consumption Rate (Ltr/Hours)', value: 'consumptionRateLtrHours' },

                    { label: 'Manual Refuel Quantity', value: 'manualRefuelQuantity' },
                    { label: 'Manual Based Fuel Consumed', value: 'manualBasedFuelConsumed' },
                    { label: 'Manual Refuel Consumption Rate (KMS/Ltr)', value: 'consumptionRateManualRefuel' },
                    { label: 'Manual Refuel Consumption Rate (Ltr/KMS)', value: 'consumptionRateManualRefuelLtrKms' },
                    { label: 'Manual Refuel Consumption Rate (Hours/Ltr)', value: 'consumptionRateManualRefuelHours' },
                    { label: 'Manual Refuel Consumption Rate (Ltr/Hours)', value: 'consumptionRateManualRefuelHoursLtr' },

                    { label: 'Green Driving', value: 'greenDriving' },
                    { label: 'Overspeeding', value: 'overspeeding' }
                ]

                // Convert to CSV
                const csv = json2csv.parse(allDownloadData, { fields })

                // Create blob and download
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
                const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss')
                saveAs(blob, `iot-device-report-${timestamp}.csv`)

                message.success('Report downloaded successfully')
            } catch (error) {
                console.error('Error downloading report:', error)
                message.error('Failed to download report. Please try again.')
            }
        } else {
            message.warning('No data available for download')
        }
    }

    processColumns = (reportData) => {
        const mainColumns = [
            {
                title: 'Asset Code',
                dataIndex: 'assetCode',
                key: 'assetCode',
                width: 120,
                // fixed: 'left',
                render: (value, record) => (
                    <Text>{record.asset && record.asset.assetCode || 'N/A'}</Text>
                )
            },
            {
                title: 'Alt Name',
                dataIndex: 'altName',
                key: 'altName',
                width: 150,
                render: (value, record) => (
                    <Text>{record.asset && record.asset.altName || 'N/A'}</Text>
                )
            },
            {
                title: 'Owner Code',
                dataIndex: 'ownerCode',
                key: 'ownerCode',
                width: 120,
                render: (value, record) => (
                    <Text>{record.asset && record.asset.ownerCode || 'N/A'}</Text>
                )
            },
            {
                title: 'Location',
                dataIndex: 'location',
                key: 'location',
                width: 120,
                render: (value, record) => (
                    <Text>{record.asset && record.asset.location && record.asset.location.name || 'N/A'}</Text>
                )
            },
            {
                title: 'Sub Location',
                dataIndex: 'subLocation',
                key: 'subLocation',
                width: 120,
                render: (value, record) => (
                    <Text>{record.asset && record.asset.client && record.asset.client.name || 'N/A'}</Text>
                )
            },
            {
                title: 'Asset Type',
                dataIndex: 'assetType',
                key: 'assetType',
                width: 120,
                render: (value, record) => (
                    <Text>{record.asset && record.asset.equipmentSubType && record.asset.equipmentSubType.assetType && record.asset.equipmentSubType.assetType.name || 'Unknown'}</Text>
                )
            },
            {
                title: 'Asset Sub Type',
                dataIndex: 'assetSubType',
                key: 'assetSubType',
                width: 130,
                render: (value, record) => (
                    <Text>{record.asset && record.asset.equipmentSubType && record.asset.equipmentSubType.name || 'Unknown'}</Text>
                )
            },
            {
                title: 'Asset Model',
                dataIndex: 'assetModel',
                key: 'assetModel',
                width: 120,
                render: (value, record) => (
                    <Text>{record.asset && record.asset.assetModel && record.asset.assetModel.name || 'Unknown'}</Text>
                )
            },
            // {
            //     title: 'Period',
            //     dataIndex: 'period',
            //     key: 'period',
            //     width: 150,
            //     render: (value, record) => (
            //         <Text>{record.startDate && record.endDate ? `${moment(record.startDate).format('DD-MM-YYYY')} to ${moment(record.endDate).format('DD-MM-YYYY')}` : 'Unknown'}</Text>
            //     )
            // },
            {
                title: 'Movement (KM)',
                dataIndex: 'movement',
                key: 'movement',
                width: 130,
                render: (value, record) => (
                    <Text>{record.distance_travelled ? Number(record.distance_travelled).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Idle Time (min)',
                dataIndex: 'idle_time_min',
                key: 'idle_time_min',
                width: 100,
                render: (value) => (
                    <Text>{value && Number(value).toFixed(2) || '0.00'}</Text>
                )
            },
            {
                title: 'Ignition Time (min)',
                dataIndex: 'ignition_time_min',
                key: 'ignition_time_min',
                width: 100,
                render: (value) => (
                    <Text>{value && Number(value).toFixed(2) || '0.00'}</Text>
                )
            },
            {
                title: 'Moving/Working Time (min)',
                dataIndex: 'moving_time_min',
                key: 'moving_time_min',
                width: 100,
                render: (value) => (
                    <Text>{value && Number(value).toFixed(2) || '0.00'}</Text>
                )
            },
            {
                title: "Avg Speed (KM/HR)",
                dataIndex: 'avg_speed',
                key: 'avg_speed',
                width: 100,
                render: (_, record) => (
                    <Text>{record.ignition_time_min && record.distance_travelled ? Number(Number(record.distance_travelled) / (Number(record.ignition_time_min) / 60)).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Fuel Consumed (ltrs)',
                dataIndex: 'fuel_consumed',
                key: 'fuel_consumed',
                width: 160,
                render: (value) => (
                    <Text>{value ? Number(value).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Fuel Consumption Rate (KMS/Ltr)',
                dataIndex: 'consumption_rate',
                key: 'consumption_rate',
                width: 180,
                render: (_, record) => (
                    <Text>{
                        record.fuel_consumed ? Number(record.fuel_consumed > 0 ? (record.distance_travelled / record.fuel_consumed) : 0).toFixed(2) : '0.00'
                    }</Text>
                )
            },
            {
                title: 'Fuel Consumption Rate (Ltr/KMS)',
                dataIndex: 'consumption_rate',
                key: 'consumption_rate',
                width: 180,
                render: (_, record) => {
                    const km_ltr = record.fuel_consumed ? Number(record.fuel_consumed > 0 ? (record.distance_travelled / record.fuel_consumed) : 0) : 0
                    if (km_ltr > 0) {
                        return <Text>{Number(1 / km_ltr).toFixed(2)}</Text>
                    }
                    return <Text>0.00</Text>

                }
            },
            {
                title: 'Consumption Rate (Hours/Ltr)',
                dataIndex: 'consumption_rate_ignition',
                key: 'consumption_rate_ignition',
                width: 180,
                render: (_, record) => (
                    <Text>{record.consumption_rate_ignition ? Number(record.consumption_rate_ignition > 0 ? (record.ignition_time_ms / (60 * 60 * 1000)) / record.consumption_rate_ignition : 0).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Consumption Rate (Ltr/Hours)',
                dataIndex: 'consumption_rate_ignition',
                key: 'consumption_rate_ignition',
                width: 180,
                render: (_, record) => {
                    const hours_ltr = record.consumption_rate_ignition ? Number(record.consumption_rate_ignition > 0 ? (record.ignition_time_ms / (60 * 60 * 1000)) / record.consumption_rate_ignition : 0) : 0
                    if (hours_ltr > 0) {
                        return <Text>{Number(1 / hours_ltr).toFixed(2)}</Text>
                    }
                    return <Text>0.00</Text>
                }
            },
            {
                title: 'Manual Refuel (ltrs)',
                dataIndex: 'manual_refuel_quantity',
                key: 'manual_refuel_quantity',
                width: 160,
                render: (value) => (
                    <Text>{value ? Number(value).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Manual Based Fuel Consumed (ltrs)',
                dataIndex: 'manual_based_fuel_consumed',
                key: 'manual_based_fuel_consumed',
                width: 160,
                render: (value) => (
                    <Text>{value ? Number(value).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Manual Refuel Consumption Rate (KMS/Ltr)',
                dataIndex: 'consumption_rate_manual_refuel',
                key: 'consumption_rate_manual_refuel',
                width: 180,
                render: (_, record) => (
                    <Text>{
                        record.manual_based_fuel_consumed ? Number(record.manual_based_fuel_consumed > 0 ? (record.distance_travelled / record.manual_based_fuel_consumed) : 0).toFixed(2) : '0.00'
                    }</Text>
                )
            },
            {
                title: 'Manual Refuel Consumption Rate (Ltr/KMS)',
                dataIndex: 'consumption_rate_manual_refuel',
                key: 'consumption_rate_manual_refuel',
                width: 180,
                render: (_, record) => {
                    const km_ltr = record.manual_based_fuel_consumed ? Number(record.manual_based_fuel_consumed > 0 ? (record.distance_travelled / record.manual_based_fuel_consumed) : 0) : 0
                    if (km_ltr > 0) {
                        return <Text>{Number(1 / km_ltr).toFixed(2)}</Text>
                    }
                    return <Text>0.00</Text>
                }
            },
            {
                title: 'Manual Refuel Consumption Rate (Hours/Ltr)',
                dataIndex: 'consumption_rate_manual_refuel_hours',
                key: 'consumption_rate_manual_refuel_hours',
                width: 180,
                render: (_, record) => (
                    <Text>{record.manual_based_fuel_consumed ? Number(record.manual_based_fuel_consumed > 0 ? (record.ignition_time_ms / (60 * 60 * 1000)) / record.manual_based_fuel_consumed : 0).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Manual Refuel Consumption Rate (Ltr/Hours)',
                dataIndex: 'consumption_rate_manual_refuel_hours',
                key: 'consumption_rate_manual_refuel_hours',
                width: 180,
                render: (_, record) => {
                    const hours_ltr = record.manual_based_fuel_consumed ? Number(record.manual_based_fuel_consumed > 0 ? (record.ignition_time_ms / (60 * 60 * 1000)) / record.manual_based_fuel_consumed : 0) : 0
                    if (hours_ltr > 0) {
                        return <Text>{Number(1 / hours_ltr).toFixed(2)}</Text>
                    }
                    return <Text>0.00</Text>
                }
            },

            {
                title: 'Possible Refuel',
                dataIndex: 'total_refuel',
                key: 'total_refuel',
                width: 130,
                render: (value) => (
                    <Text>{value ? Number(value).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Possible Drain',
                dataIndex: 'total_theft',
                key: 'total_theft',
                width: 130,
                render: (value) => (
                    <Text>{value ? Number(value).toFixed(2) : '0.00'}</Text>
                )
            },
            {
                title: 'Green Driving',
                dataIndex: 'green_driving_summary',
                key: 'green_driving',
                width: 120,
                render: (_, record) => {
                    const g = record.green_driving_summary || record.green_drivng_summery
                    if (!g) return null
                    const total = g && g.counts ? (Number(g.counts[1] || 0) + Number(g.counts[2] || 0) + Number(g.counts[3] || 0)) : 0
                    if (total > 0) {
                        return (
                            <a onClick={(e) => { e.preventDefault(); this.setState({ greenDrivingModalRecord: record }) }} href="#">
                                <Badge count={total} />
                            </a>
                        )
                    }
                    return <Text>0</Text>
                }
            },
            {
                title: 'Overspeeding',
                dataIndex: 'overspeed_summary',
                key: 'overspeeding',
                width: 120,
                render: (_, record) => {
                    const o = record.overspeed_summary
                    if (!o) return null
                    const total = Number(o.total_overspeed_events || 0)
                    if (total > 0) {
                        return (
                            <a onClick={(e) => { e.preventDefault(); this.setState({ overspeedModalRecord: record }) }} href="#">
                                <Badge count={total} />
                            </a>
                        )
                    }
                    return <Text>0</Text>
                }
            }
        ]

        return mainColumns
    }

    render() {
        const { getFieldDecorator } = this.props.form
        const { loading, assetFilters } = this.props
        const { reportData, reportLoading } = this.state
        // console.log(this.props.assetData)

        const { assetTypes, assetSubTypes, assetModels } = assetFilters || {}

        return (
            <div>
                <Spin spinning={loading || reportLoading}>
                    <Card
                        className={`gx-card-metrics`}
                        title={`IoT Device Report`}
                    >
                        <Form
                            onSubmit={this.handleSubmit}
                            className="ant-advanced-search-form"
                            layout="inline"
                            labelAlign="left"
                        >
                            <Row gutter={8}>
                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item key={'location'} label={'Location'} style={{ width: '100%' }}>
                                        {getFieldDecorator(`location`, {
                                            initialValue: [],
                                            rules: [{ required: true, message: `Location is required!` }],
                                        })(<Select
                                            mode="multiple"
                                            showSearch
                                            showArrow
                                            filterOption={(input, option) =>
                                                (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                            }
                                            placeholder={`Select Location`}
                                            allowClear
                                            onChange={(value) => {
                                                // If "all" is selected, clear other selections
                                                if (value && value.includes('all')) {
                                                    this.props.form.setFieldsValue({ location: ['all'] })
                                                }
                                            }}
                                        >
                                            <Select.Option key={'AllLocation'}
                                                value={'all'}
                                                disabled={this.props.form.getFieldValue('location') && this.props.form.getFieldValue('location').length > 0 && !this.props.form.getFieldValue('location').includes('all')}
                                            >All</Select.Option>
                                            {(this.props.authUser && this.props.authUser.locations || []).map(item =>
                                                <Select.Option key={item._id}
                                                    value={item._id}
                                                    disabled={this.props.form.getFieldValue('location') && this.props.form.getFieldValue('location').includes('all')}
                                                >{item.name}</Select.Option>)}

                                        </Select>)
                                        }
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item key={'subLocation'} label={'Sub Location'} style={{ width: '100%' }}>
                                        {getFieldDecorator(`subLocation`, {
                                            initialValue: [],
                                            rules: [{ required: true, message: `Sub Location is required!` }],
                                        })(<Select
                                            mode="multiple"
                                            showSearch
                                            showArrow
                                            filterOption={(input, option) =>
                                                (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                            }
                                            placeholder={`Select Sub Location`}
                                            allowClear
                                            onChange={(value) => {
                                                // If "all" is selected, clear other selections
                                                if (value && value.includes('all')) {
                                                    this.props.form.setFieldsValue({ subLocation: ['all'] })
                                                }
                                            }}
                                        >
                                            <Select.Option key={'AllSubLocation'}
                                                value={'all'}
                                                disabled={this.props.form.getFieldValue('subLocation') && this.props.form.getFieldValue('subLocation').length > 0 && !this.props.form.getFieldValue('subLocation').includes('all')}
                                            >All</Select.Option>
                                            {(this.props.authUser && this.props.authUser.clients || []).map(item =>
                                                <Select.Option key={item._id}
                                                    value={item._id}
                                                    disabled={this.props.form.getFieldValue('subLocation') && this.props.form.getFieldValue('subLocation').includes('all')}
                                                >{item.name}</Select.Option>)}

                                        </Select>)
                                        }
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item
                                        key={"periodFromTo"}
                                        label={"Select Period"}
                                        style={{ width: "100%" }}
                                    >
                                        {getFieldDecorator(`periodFromTo`, {
                                            rules: [
                                                { required: true, message: `Period From To is required!` }
                                            ],
                                            initialValue: null
                                        })(<RangePicker style={{ width: "100%" }} />)}
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item
                                        key={"assetType"}
                                        label={"Asset Type"}
                                        style={{ width: "100%" }}
                                    >
                                        {getFieldDecorator(`assetType`, {
                                            initialValue: [],
                                            onChange: (value) => {
                                                // Reset subType and model when type changes
                                                this.props.form.setFieldsValue({
                                                    assetSubType: [],
                                                    assetModel: []
                                                })

                                                // If "all" is selected, clear other selections
                                                if (value && value.includes('all')) {
                                                    this.props.form.setFieldsValue({ assetType: ['all'] })
                                                }
                                            }
                                        })(
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                showArrow
                                                filterOption={(input, option) =>
                                                    (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                                }
                                                placeholder={`Select Asset Type`}
                                                allowClear
                                            >
                                                <Select.Option key={'AllType'}
                                                    value={'all'}
                                                    disabled={this.props.form.getFieldValue('assetType') && this.props.form.getFieldValue('assetType').length > 0 && !this.props.form.getFieldValue('assetType').includes('all')}
                                                >All</Select.Option>
                                                {(assetTypes || []).map(item =>
                                                    <Select.Option key={item._id}
                                                        value={item._id}
                                                        disabled={this.props.form.getFieldValue('assetType') && this.props.form.getFieldValue('assetType').includes('all')}
                                                    >{item.name}</Select.Option>)}

                                            </Select>
                                        )}
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item
                                        key={"assetSubType"}
                                        label={"Asset Sub Type"}
                                        style={{ width: "100%" }}
                                    >
                                        {getFieldDecorator(`assetSubType`, {
                                            initialValue: [],
                                            onChange: (value) => {
                                                // Reset model when subType changes
                                                this.props.form.setFieldsValue({
                                                    assetModel: []
                                                })

                                                // If "all" is selected, clear other selections
                                                if (value && value.includes('all')) {
                                                    this.props.form.setFieldsValue({ assetSubType: ['all'] })
                                                }
                                            }
                                        })(
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                showArrow
                                                filterOption={(input, option) =>
                                                    (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                                }
                                                placeholder={`Select Asset Sub Type`}
                                                allowClear
                                            >
                                                <Select.Option key={'AllSubType'}
                                                    value={'all'}
                                                    disabled={this.props.form.getFieldValue('assetSubType') && this.props.form.getFieldValue('assetSubType').length > 0 && !this.props.form.getFieldValue('assetSubType').includes('all')}
                                                >All</Select.Option>
                                                {(assetSubTypes || [])
                                                    .filter(item => {
                                                        const selectedAssetType = this.props.form.getFieldValue('assetType')
                                                        return !selectedAssetType || selectedAssetType.length === 0 || selectedAssetType.includes('all') || selectedAssetType.includes(item.assetType)
                                                    })
                                                    .map(item =>
                                                        <Select.Option key={item._id}
                                                            value={item._id}
                                                            disabled={this.props.form.getFieldValue('assetSubType') && this.props.form.getFieldValue('assetSubType').includes('all')}
                                                        >{item.name}</Select.Option>)}

                                            </Select>
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item
                                        key={"assetModel"}
                                        label={"Asset Model"}
                                        style={{ width: "100%" }}
                                    >
                                        {getFieldDecorator(`assetModel`, {
                                            initialValue: [],
                                            onChange: (value) => {
                                                // If "all" is selected, clear other selections
                                                if (value && value.includes('all')) {
                                                    this.props.form.setFieldsValue({ assetModel: ['all'] })
                                                }
                                            }
                                        })(
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                showArrow
                                                filterOption={(input, option) =>
                                                    (option.props.children + "").toLowerCase().indexOf((input + "").toLowerCase()) >= 0
                                                }
                                                placeholder={`Select Asset Model`}
                                                allowClear
                                            >
                                                <Select.Option key={'AllModel'}
                                                    value={'all'}
                                                    disabled={this.props.form.getFieldValue('assetModel') && this.props.form.getFieldValue('assetModel').length > 0 && !this.props.form.getFieldValue('assetModel').includes('all')}
                                                >All</Select.Option>
                                                {(assetModels || [])
                                                    .filter(item => {
                                                        const selectedAssetType = this.props.form.getFieldValue('assetType')
                                                        const selectedAssetSubType = this.props.form.getFieldValue('assetSubType')

                                                        // If both type and subtype are empty or include 'all', show all models
                                                        if ((!selectedAssetType || selectedAssetType.length === 0 || selectedAssetType.includes('all')) &&
                                                            (!selectedAssetSubType || selectedAssetSubType.length === 0 || selectedAssetSubType.includes('all'))) return true

                                                        // If subtype is selected (regardless of asset type), filter by subtype
                                                        if (selectedAssetSubType && selectedAssetSubType.length > 0 && !selectedAssetSubType.includes('all')) {
                                                            return selectedAssetSubType.includes(item.assetSubType)
                                                        }

                                                        // If only asset type is selected (subtype is empty or 'all'), show models for all subtypes of selected type
                                                        if (selectedAssetType && selectedAssetType.length > 0 && !selectedAssetType.includes('all')) {
                                                            const validSubTypes = assetSubTypes
                                                                .filter(st => selectedAssetType.includes(st.assetType))
                                                                .map(st => st._id)
                                                            return validSubTypes.includes(item.assetSubType)
                                                        }

                                                        return true
                                                    })
                                                    .map(item =>
                                                        <Select.Option key={item._id}
                                                            value={item._id}
                                                            disabled={this.props.form.getFieldValue('assetModel') && this.props.form.getFieldValue('assetModel').includes('all')}
                                                        >{item.name}</Select.Option>)}

                                            </Select>
                                        )}
                                    </Form.Item>
                                </Col>

                                {/* <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item
                                        key={"frequency"}
                                        label={"Frequency"}
                                        style={{ width: "100%" }}
                                    >
                                        {getFieldDecorator(`frequency`, {
                                            initialValue: 'Daily'
                                        })(
                                            <Select
                                                placeholder={`Select Frequency`}
                                                onChange={(value) => {
                                                    this.setState({ interval: value })
                                                }}
                                                initialValue={this.state.interval || 'd'}
                                            >
                                                <Select.Option value={'d'}>Daily</Select.Option>
                                                <Select.Option value={'w'}>Weekly</Select.Option>
                                                <Select.Option value={'m'}>Monthly</Select.Option>
                                                <Select.Option value={'y'}>Annually</Select.Option>
                                            </Select>
                                        )}
                                    </Form.Item>
                                </Col> */}

                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item key={'asset'} label={'(OR) Search Asset'} style={{ width: '100%' }}>
                                        {getFieldDecorator(`asset`, {
                                            initialValue: [],
                                            rules: [{ required: false, message: `Asset is required!` }],
                                        })(<AssetSearch
                                            styleName="gx-lt-icon-search-bar-lg"
                                            placeholder="Or search any Asset..."
                                            onChange={this.props.searchGlobalAssetData}
                                            assetData={
                                                this.props.assetData && this.props.assetData.filter((ast) => ast.assetModel)
                                            } // only assets with assetModel
                                            onSelect={this.handleAssetSelect}
                                            authUser={this.props.authUser}
                                            loading={this.props.assetSearchLoading}
                                            multiple={true}
                                        />)}
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                                    <Form.Item style={{ marginTop: '25px' }}>
                                        <span style={{ display: 'flex', flexDirection: 'row' }}>
                                            <Button type="primary" htmlType="submit" loading={loading || reportLoading}>
                                                Generate Report
                                            </Button>
                                            <Button disabled={!reportData || !reportData.length}
                                                type="default" htmlType="button" loading={loading} icon={<DownloadOutlined />}
                                                onClick={() => this.downloadReport(reportData)}>
                                                Download
                                            </Button>
                                        </span>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        {reportData && reportData.length ? (
                            <div style={{ marginTop: '24px' }}>
                                <Title level={4}>Report Results</Title>
                                <Spin spinning={reportLoading} tip={reportLoading ? 'Regenerating report...' : ''}>
                                    <Table
                                        dataSource={reportData}
                                        columns={this.processColumns(reportData)}
                                        bordered
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showQuickJumper: true,
                                            showTotal: (total, range) =>
                                                `${range[0]}-${range[1]} of ${total} items`
                                        }}
                                        rowKey={(record) => record.key || `${record.assetId}-${record.date}`}
                                        scroll={{ x: 1500 }}
                                        expandedRowRender={(record) => this.expandedRowRender(record)}
                                        expandedRowKeys={this.state.expandedRowKeys}
                                        onExpandedRowsChange={(expandedRowKeys) => {
                                            this.setState({ expandedRowKeys })
                                        }}
                                    />
                                </Spin>
                            </div>
                        ) : (loading || reportLoading) ? <Skeleton active /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    </Card>

                    {this.state.greenDrivingModalRecord && this.renderGreenDrivingModal()}
                    {this.state.overspeedModalRecord && this.renderOverspeedModal()}
                </Spin>
            </div>
        )
    }

    renderGreenDrivingModal = () => {
        const record = this.state.greenDrivingModalRecord
        if (!record || !record.green_driving_summary) return null
        const g = record.green_driving_summary
        const counts = g.counts || { 1: 0, 2: 0, 3: 0 }
        const logs = g.logs || { 1: [], 2: [], 3: [] }
        const labels = { 1: 'Harsh Acceleration', 2: 'Harsh Braking', 3: 'Harsh Cornering' }
        const columns = [
            { title: 'Time', dataIndex: 'timestamp', key: 'ts', render: (t) => t != null ? moment(t).format('DD/MM/YYYY HH:mm:ss') : '-' },
            { title: 'Location', dataIndex: 'lat_long', key: 'loc', render: (v) => v || '-' }
        ]
        return (
            <Modal
                title="Green Driving Events"
                visible={true}
                onCancel={() => this.setState({ greenDrivingModalRecord: null })}
                footer={null}
                width={700}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text strong>{formatAssetLabel(record.asset)}</Text>
                </div>
                {[1, 2, 3].map((k) => (
                    <div key={k} style={{ marginBottom: 24 }}>
                        <Text strong>{labels[k]} ({counts[k] || 0})</Text>
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={(logs[k] || []).map((row, i) => ({ ...row, key: `${k}-${i}` }))}
                            pagination={false}
                            style={{ marginTop: 8 }}
                        />
                    </div>
                ))}
            </Modal>
        )
    }

    renderOverspeedModal = () => {
        const record = this.state.overspeedModalRecord
        if (!record || !record.overspeed_summary) return null
        const o = record.overspeed_summary
        const events = o.overspeed_events || []
        const columns = [
            { title: 'Speed Limit(Km/h)', key: 'speedLimit', render: () => (o.speed_limit != null && o.speed_limit !== '') ? Number(o.speed_limit) : '-' },
            { title: 'Duration (s)', dataIndex: 'duration', key: 'dur', render: (v) => v != null ? (Number(v) / 1000).toFixed(2) : '-' },
            { title: 'Avg Speed', dataIndex: 'average_speed', key: 'avg', render: (v) => v != null ? Number(v).toFixed(2) : '-' },
            { title: 'Max Speed', dataIndex: 'max_speed', key: 'max', render: (v) => v != null ? Number(v).toFixed(2) : '-' },
            { title: 'Start', dataIndex: 'start_timestamp', key: 'start', render: (v) => v != null ? moment(v).format('DD/MM/YYYY HH:mm:ss') : '-' },
            { title: 'End', dataIndex: 'end_timestamp', key: 'end', render: (v) => v != null ? moment(v).format('DD/MM/YYYY HH:mm:ss') : '-' },
            { title: 'Start Location', dataIndex: 'start_location', key: 'startLoc', render: (v) => v || '-' },
            { title: 'End Location', dataIndex: 'end_location', key: 'endLoc', render: (v) => v || '-' }
        ]
        return (
            <Modal
                title="Overspeed Events"
                visible={true}
                onCancel={() => this.setState({ overspeedModalRecord: null })}
                footer={null}
                width={900}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text strong>{formatAssetLabel(record.asset)}</Text>
                    {o.speed_limit != null && <Text style={{ marginLeft: 12 }}>Speed limit: {Number(o.speed_limit)}</Text>}
                </div>
                <Table
                    size="small"
                    columns={columns}
                    dataSource={events.map((row, i) => ({ ...row, key: i }))}
                    pagination={{ pageSize: 10 }}
                />
            </Modal>
        )
    }
}

const mapStateToProps = ({ auth, commonData }) => {
    return {
        authUser: auth.authUser,
        loading: commonData.loading,
        assetFilters: commonData.assetFilters,
        assetData: commonData.assetSearchData,
        assetSearchLoading: commonData.assetSearchLoading,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        fetchAssetFiltersData,
        searchGlobalAssetData
    }
}

const IotDeviceReportForm = Form.create({ name: 'iot-device-report' })(IotDeviceReport)
export default connect(mapStateToProps, mapDispatchToProps())(IotDeviceReportForm)
